import type { ReactNode } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { EMPTY_VALUE } from "@/constants";
import { HcbbInfoHint } from "@/pages/components/hcbb-cost-estimator/hcbb-info-hint";
import { parseMoney } from "@/lib/hcbb-cost-estimator-utils";
import type { HcbbPriceHighlight } from "@/model/hcbb-cost-estimator";

/**
 * What the plan generally allows for a procedure in the member's area, and
 * their share of it. Sits beside the result rows as the benchmark to read them
 * against, so it renders whenever HCBB has a priced benchmark — even when no
 * provider matched the search.
 */
export const HcbbPriceHighlightCard = ({
  highlight,
}: {
  highlight: HcbbPriceHighlight;
}) => {
  // A section with no parseable fee is a heading with nothing under it.
  const pricingSections = (highlight.pricingSections ?? []).filter(
    (section) => parseMoney(section?.fee) !== null,
  );

  return (
    <div className="border-1 space-y-4 rounded-lg border p-4 shadow-md">
      <h2 className="font-medium">Highlight: {highlight.procedureName}</h2>

      <PriceFigure
        label="Typical Allowed Amount"
        value={highlight.medianPrice}
        hintLabel="About the typical allowed amount"
      >
        The &quot;Allowed Amount&quot; is the maximum dollar limit a health plan
        agrees to pay for a specific medical service. Your estimated
        out-of-pocket cost is calculated based on this information.
      </PriceFigure>

      <PriceRangeMeter
        max={highlight.maxPrice}
        median={highlight.medianPrice}
        min={highlight.minPrice}
      />

      <PriceFigure
        label="Typical Member Cost Estimate"
        value={highlight.estimatedCost}
        hintLabel="About the typical member cost estimate"
      >
        Member cost estimates are calculated based on the allowed amount, plan
        provisions, deductible, and out-of-pocket status.
      </PriceFigure>

      {pricingSections.length > 0 && (
        <>
          <hr />
          {pricingSections.map((section, index) => (
            <div
              key={section.type || index}
              className="flex items-center justify-between text-sm"
            >
              <p>
                <span
                  // Each step down the list fades another 10%, floored so the
                  // dot never disappears on a long breakdown.
                  style={{ opacity: Math.max(1 - index * 0.1, 0.3) }}
                  className="mr-[8px] inline-block h-[8px] w-[8px] rounded-full bg-primary"
                />
                {section?.type || EMPTY_VALUE}
              </p>
              <p className="font-medium">{section.fee}</p>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

/** A labelled price with the tooltip that explains what it covers. */
const PriceFigure = ({
  label,
  value,
  hintLabel,
  children,
}: {
  label: string;
  value?: string | null;
  hintLabel: string;
  children: ReactNode;
}) => (
  <div>
    <p className="flex items-center gap-1 text-sm text-gray-800">
      {label}
      <HcbbInfoHint label={hintLabel}>{children}</HcbbInfoHint>
    </p>
    <p className="text-large font-bold text-primary">{value || EMPTY_VALUE}</p>
  </div>
);

/**
 * Where the typical price sits between the local low and high — a single value
 * in a range, so a linear meter rather than a chart. The track is one hue
 * light-to-dark off the themeable `primary` token; the low/high figures are
 * labelled directly, so nothing is carried by colour alone.
 */
const PriceRangeMeter = ({
  min,
  max,
  median,
}: {
  min?: string | null;
  max?: string | null;
  median?: string | null;
}) => {
  const low = parseMoney(min);
  const typical = parseMoney(median);
  const high = parseMoney(max);

  if (low === null || typical === null || high === null) return null;

  const span = high - low;
  const ratio = span > 0 ? (typical - low) / span : 0.5;
  const position = Math.min(Math.max(ratio, 0), 1) * 100;

  return (
    <div>
      <div
        role="meter"
        aria-label="Typical allowed amount within the local price range"
        aria-valuemin={low}
        aria-valuemax={high}
        aria-valuenow={typical}
        aria-valuetext={`${median}, between ${min} and ${max}`}
        className="relative h-2 w-full rounded-full bg-gray-200"
      >
        {/* The gradient runs only as far as the indicator; the track behind it
            carries the rest of the range. */}
        <div
          style={{ width: `${position}%` }}
          className="h-full rounded-full bg-gradient-to-r from-primary-light to-primary"
        />

        <Tooltip>
          <TooltipTrigger asChild>
            {/* Decorative handle: the meter role above already announces the
                value, so it stays out of the accessibility tree. */}
            <button
              type="button"
              aria-hidden="true"
              tabIndex={-1}
              style={{ left: `${position}%` }}
              className="absolute top-1/2 size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-primary bg-white shadow"
            />
          </TooltipTrigger>
          <TooltipContent>Typical: {median}</TooltipContent>
        </Tooltip>
      </div>
      <div className="mt-2 flex justify-between text-xs text-gray-600">
        <span>Low: {min}</span>
        <span>High: {max}</span>
      </div>
    </div>
  );
};
